import Game from "@Game";
import BaseScene from '@Scenes/BaseScene';

/*--------------- Config ------------------------*/
import * as Scenes from '@Config/scenes';
import Network from '@Networks/Localhost';
import { Leaderboards, ILeaderboardConfig } from '@Config/leaderboards';

/*--------------- Language Manager ------------------------*/
import LanguageManager from '@Languages/LanguageManager';
import EventEmitter from '@Utils/Events/EventEmitter';

/**
 * Score Scene,
 * Displays score and tricks
 */
class LeaderboardsScene extends BaseScene
{
    private loadingLeaderboard: boolean;

    private leaderboard: ILeaderboardConfig;
    private leaderboards: ILeaderboardConfig[];

    private topRightHitArea: Phaser.Geom.Rectangle;
    private topLeftHitArea: Phaser.Geom.Rectangle;

    private leftLeaderboardTitle: Phaser.GameObjects.Text;
    private centerLeaderboardTitle: Phaser.GameObjects.Text;
    private rightLeaderboardTitle: Phaser.GameObjects.Text;
    private loadingCircle: Phaser.GameObjects.Graphics;

    /**
     * Contructor
     */
    constructor()
    {
        super(Scenes.LEADERBOARDS)
    }

    /**
     * Creates score scene
     */
    public async create()
    {

        this.leaderboard = Leaderboards.GLOBAL;

        const leaderboards = [Leaderboards.GLOBAL, Leaderboards.GLOBAL_FRIENDS, Leaderboards.WEEKLY];

        if (Network.context.getID())
        {
            leaderboards.push(Leaderboards.CONTEXTUAL);
        }

        this.leaderboards = leaderboards;

        this.subscribeToEvents();
        this.enableShutdown();
        this.createTitles();
        this.createLoadingCircle();
        this.launchCurrentLeaderboard();

        this.loadingLeaderboard = false;
    }

    /**
     * Show loading circle
     */
    public startLoadingLeaderboard()
    {
        this.loadingLeaderboard = true;
        this.loadingCircle.setVisible(true);
    }

    /**
     * Subcribe to events
     */
    public subscribeToEvents()
    {
        EventEmitter.on(Scenes.LEADERBOARD, 'loading_complete', this.stopLoadingLeaderboard.bind(this));
    }

    /**
     * Hide loading circle
     */
    public stopLoadingLeaderboard()
    {
        this.loadingLeaderboard = false;
        this.loadingCircle.setVisible(false);
    }

    /**
     * Update
     */
    public update()
    {
        this.loadingCircle.angle += 10;
    }

    /**
     * Create loading circle
     */
    private createLoadingCircle()
    {
        const gameSize = this.getGameSize();

        const loadingCircle = this.add.graphics();
        loadingCircle.lineStyle(2, 0x000000);
        loadingCircle.beginPath();
        loadingCircle.arc(0, 0, 10, 0, (Math.PI * 2) * 0.85);
        loadingCircle.strokePath();

        loadingCircle.x = gameSize.width / 2;
        loadingCircle.y = 200;

        this.loadingCircle = loadingCircle;
    }

    /**
     * Returns the next leaderboard ID
     */
    private getNextLeaderboard(): ILeaderboardConfig
    {
        const leaderboards = this.leaderboards;
        const leaderboardCount = leaderboards.length;
        const currentLeaderboardIndex = leaderboards.indexOf(this.leaderboard);

        let nextLeaderboardIndex = currentLeaderboardIndex + 1;
        if (nextLeaderboardIndex === leaderboardCount)
        {
            nextLeaderboardIndex = 0;
        }

        return leaderboards[nextLeaderboardIndex];
    }

    /**
     * Returns the previous leaderboard ID
     */
    private getPreviousLeaderboard(): ILeaderboardConfig
    {
        const leaderboards = this.leaderboards;
        const leaderboardCount = leaderboards.length;
        const currentLeaderboardIndex = leaderboards.indexOf(this.leaderboard);

        let previousLeaderboardIndex = currentLeaderboardIndex - 1;

        if (previousLeaderboardIndex < 0)
        {
            previousLeaderboardIndex = leaderboardCount - 1;
        }

        return leaderboards[previousLeaderboardIndex];
    }

    /**
     * Create titles
     */
    public createTitles()
    {
        const gameSize = this.getGameSize();

        const offsetY = 15;
        const fontStyle = { fontFamily: 'Permanent Marker' };
        const titleLang = LanguageManager.getLanguage('leaderboards-title');

        const titleText = this.add.text(gameSize.centerX, 50 + offsetY, titleLang, fontStyle);
        titleText.setOrigin(0.5);
        titleText.setFontSize(40);
        titleText.setAlign('center');
        titleText.setColor('#000000');
        titleText.alpha = 0;

        const leftLeaderboardTitle = this.add.text(50, 115, '', fontStyle);
        leftLeaderboardTitle.setOrigin(0, 0);
        leftLeaderboardTitle.setFontSize(30);
        leftLeaderboardTitle.setAlign('left');
        leftLeaderboardTitle.setColor('#000000');

        const centerLeaderboardTitle = this.add.text(gameSize.width / 2, 115, '', fontStyle);
        centerLeaderboardTitle.setOrigin(0.5, 0);
        centerLeaderboardTitle.setFontSize(30);
        centerLeaderboardTitle.setAlign('center');
        centerLeaderboardTitle.setColor('#000000');

        const rightLeaderboardTitle = this.add.text(gameSize.width - 50, 115, '', fontStyle);
        rightLeaderboardTitle.setOrigin(1, 0);
        rightLeaderboardTitle.setFontSize(30);
        rightLeaderboardTitle.setAlign('right');
        rightLeaderboardTitle.setColor('#000000');

        this.leftLeaderboardTitle = leftLeaderboardTitle;
        this.centerLeaderboardTitle = centerLeaderboardTitle;
        this.rightLeaderboardTitle = rightLeaderboardTitle;

        this.topLeftHitArea = new Phaser.Geom.Rectangle(0, 80, 300, 100);
        this.topRightHitArea = new Phaser.Geom.Rectangle(gameSize.width - 300, 80, 300, 100);

        this.input.on('pointerdown', this.gotoLeaderboard.bind(this));

        this.add.tween({ targets: titleText, alpha: 1, duration: 500 });
        this.setLeaderboardTitles();
    }

    /**
     * Go to leaderboard
     **/
    private gotoLeaderboard(pointer, gameobjects)
    {
        if (this.topLeftHitArea.contains(pointer.x, pointer.y))
        {
            this.gotoPreviousLeaderboard();
        }
        else if (this.topRightHitArea.contains(pointer.x, pointer.y))
        {
            this.gotoNextLeaderboard();
        }
    }

    /**
     * Shows next leaderboard
     */
    private async gotoNextLeaderboard()
    {
        if (this.loadingLeaderboard === false)
        {
            const nextLeaderboard = this.getNextLeaderboard();
            this.leaderboard = nextLeaderboard;
            this.setLeaderboardTitles();
            this.launchCurrentLeaderboard();
        }
    }

    /**
     * Shows previous leaderboard
     */
    private async gotoPreviousLeaderboard()
    {
        if (this.loadingLeaderboard === false)
        {
            const previousLeaderboard = this.getPreviousLeaderboard();
            this.leaderboard = previousLeaderboard;
            this.setLeaderboardTitles();
            this.launchCurrentLeaderboard();
        }
    }

    /**
     * Launches the current leaderboard
     */
    private async launchCurrentLeaderboard()
    {
        this.startLoadingLeaderboard();

        this.scene.stop(Scenes.LEADERBOARD);

        const leaderboardEntries = await this.getLeaderboardEntries();

        if( this.scene.isActive(Scenes.LEADERBOARDS))
        {
            this.scene.launch(Scenes.LEADERBOARD, {
                leaderboard: this.leaderboard,
                leaderboardEntries: leaderboardEntries.slice(0, 5)
            });
        }
    }

    /**
     * Get leaderboard entries
     * This needs to be done before launching the leaderboard.
     * This is due to the fact that the 'preload' method of the phaser scene lifecycle
     * cannot be async if you want to preload assets (player iamges etc)
     */
    private async getLeaderboardEntries()
    {
        const leaderboard = Game.leaderboards[this.leaderboard.id];

        let leaderboardEntries = [];

        if (this.leaderboard.friends)
        {
            leaderboardEntries = await leaderboard.getConnectedPlayerEntriesAsync(5, 0);
        }
        else
        {
            leaderboardEntries = await leaderboard.getEntriesAsync(5, 0);
        }

        const playerLeaderboardEntry = await this.getPlayerLeaderboardEntry(this.leaderboard);

        if( playerLeaderboardEntry )
        {
            this.addPlayerToLeaderboard(playerLeaderboardEntry, leaderboardEntries);
        }

        return leaderboardEntries;
    }


    /**
     * Returns player leaderboard entry
     */
    private async getPlayerLeaderboardEntry(leaderboardConfig: ILeaderboardConfig)
    {
        return await Game.leaderboards[leaderboardConfig.id].getPlayerEntryAsync();
    }

    /**
     *
     * @param playerEntry
     * @param leaderboardEntries
     */
    private addPlayerToLeaderboard(playerEntry: Networks.LeaderboardEntry, leaderboardEntries: Networks.LeaderboardEntry[]): Networks.LeaderboardEntry[]
    {
        const player = playerEntry.getPlayer();

        /**
         * Check to see if player is already part of the leaderboard
         */
        const containsPlayer = leaderboardEntries.reduce((contains, leaderboardEntry) =>
        {
            if (contains === false)
            {
                const leaderboardPlayer = leaderboardEntry.getPlayer();
                contains = leaderboardPlayer.getID() === player.getID();
            }
            return contains;
        }, false)

        /**
         * If the entries
         */
        if (containsPlayer === false)
        {
            leaderboardEntries.splice(leaderboardEntries.length - 1, 1, playerEntry);
        }

        return leaderboardEntries
    };


    /**
     * Sets leaderboard titles
     */
    private setLeaderboardTitles()
    {
        const currentLeaderboard = this.leaderboard;
        const nextLeaderboard = this.getNextLeaderboard();
        const previousLeaderboard = this.getPreviousLeaderboard();

        const leftLeaderboardTitleLang = '< ' + LanguageManager.getLanguage(`leaderboard-title-${previousLeaderboard.key}`);
        const centerLeaderboardTitleLang = LanguageManager.getLanguage(`leaderboard-title-${currentLeaderboard.key}`);
        const rightLeaderboardTitleLang = LanguageManager.getLanguage(`leaderboard-title-${nextLeaderboard.key}`) + ' >';

        this.leftLeaderboardTitle.setText(leftLeaderboardTitleLang);
        this.centerLeaderboardTitle.setText(centerLeaderboardTitleLang);
        this.rightLeaderboardTitle.setText(rightLeaderboardTitleLang);
    }

    /**
     * Shuts down the scene
     */
    public shutdown()
    {
        EventEmitter.off(Scenes.LEADERBOARD, 'loading_complete');
        this.scene.stop(Scenes.LEADERBOARD);
    }

}

export default LeaderboardsScene;